import React, { useEffect, useState } from "react";
import { ShieldCheck, Plus, Pencil, Users as UsersIcon, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PageHeader, Loading, Field } from "@/components/common";
import { DataTable, Btn, TextInput, Select } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Modal } from "@/components/Modal";

export default function Users() {
  return (
    <div>
      <PageHeader title="Users & Roles" subtitle="Manage access, assign roles and configure granular permissions" icon={ShieldCheck} />
      <Tabs defaultValue="users">
        <TabsList className="mb-4">
          <TabsTrigger value="users" data-testid="tab-users"><UsersIcon size={15} className="mr-1.5" /> Users</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles"><KeyRound size={15} className="mr-1.5" /> Roles & Permissions</TabsTrigger>
        </TabsList>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="roles"><RolesTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function UsersTab() {
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [bus, setBus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role_id: "", status: "Active", business_unit_ids: [] });

  const load = async () => { setLoading(true); try { const { data } = await api.get("/users"); setRows(data); } finally { setLoading(false); } };
  useEffect(() => { api.get("/roles").then((r) => setRoles(r.data)); api.get("/business-units").then((r) => setBus(r.data)); load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", password: "", role_id: roles[0]?.id || "", status: "Active", business_unit_ids: [] }); setModal(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...u, password: "" }); setModal(true); };

  const save = async () => {
    if (!form.name || !form.email || !form.role_id) return toast.error("Name, email and role required");
    try {
      if (editing) await api.put(`/users/${editing.id}`, form);
      else await api.post("/users", form);
      toast.success("User saved"); setModal(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const columns = [
    { key: "name", label: "Name", className: "font-medium text-slate-800", render: (r) => <div>{r.name} {r.is_demo && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 rounded font-medium ml-1">DEMO</span>}</div> },
    { key: "email", label: "Email" },
    { key: "role_name", label: "Role", render: (r) => <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{r.role_name}</span> },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "actions", label: "", align: "right", render: (r) => <button onClick={(e) => { e.stopPropagation(); openEdit(r); }} className="p-1.5 text-slate-400 hover:text-seq-green" data-testid={`edit-user-${r.id}`}><Pencil size={15} /></button> },
  ];

  return (
    <div>
      <div className="flex justify-end mb-3"><Btn onClick={openNew} data-testid="new-user-btn"><Plus size={16} /> New User</Btn></div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <Loading /> : <DataTable columns={columns} rows={rows} rowTestId="user-row" />}
      </div>
      <Modal open={modal} onOpenChange={setModal} title={editing ? "Edit user" : "New user"}
        footer={<><Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn><Btn onClick={save} data-testid="save-user-btn">Save</Btn></>}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Name" required><TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Email" required><TextInput type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} /></Field>
          <Field label="Role" required><Select value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })}><option value="">Select</option>{roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</Select></Field>
          <Field label="Status"><Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option>Active</option><option>Disabled</option></Select></Field>
          <Field label={editing ? "New password (optional)" : "Password"} className="col-span-2"><TextInput type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "Leave blank to keep current" : "Set a password"} /></Field>
        </div>
      </Modal>
    </div>
  );
}

function RolesTab() {
  const [roles, setRoles] = useState([]);
  const [catalogue, setCatalogue] = useState({});
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", permissions: [] });

  const load = async () => { setLoading(true); try { const { data } = await api.get("/roles"); setRoles(data); } finally { setLoading(false); } };
  useEffect(() => { api.get("/permissions/catalogue").then((r) => setCatalogue(r.data.groups)); load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", description: "", permissions: [] }); setModal(true); };
  const openEdit = (r) => { setEditing(r); setForm({ name: r.name, description: r.description, permissions: r.permissions.includes("*") ? Object.values(catalogue).flatMap((g) => Object.keys(g)) : r.permissions }); setModal(true); };

  const toggle = (p) => setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));

  const save = async () => {
    if (!form.name) return toast.error("Role name required");
    try {
      if (editing) await api.put(`/roles/${editing.id}`, form);
      else await api.post("/roles", form);
      toast.success("Role saved"); setModal(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <Loading />;
  return (
    <div>
      <div className="flex justify-end mb-3"><Btn onClick={openNew} data-testid="new-role-btn"><Plus size={16} /> Custom Role</Btn></div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map((r) => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-medium text-slate-800 flex items-center gap-2">{r.name} {r.is_system && <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 rounded">SYSTEM</span>}</div>
                <div className="text-xs text-slate-400 mt-0.5">{r.user_count} user{r.user_count !== 1 ? "s" : ""}</div>
              </div>
              <button onClick={() => openEdit(r)} className="p-1.5 text-slate-400 hover:text-seq-green" data-testid={`edit-role-${r.id}`}><Pencil size={15} /></button>
            </div>
            <p className="text-sm text-slate-500 mt-2">{r.description}</p>
            <div className="mt-2 text-xs text-slate-400">{r.permissions.includes("*") ? "All permissions" : `${r.permissions.length} permissions`}</div>
          </div>
        ))}
      </div>

      <Modal open={modal} onOpenChange={setModal} title={editing ? `Edit role — ${editing.name}` : "New custom role"} wide testId="role-modal"
        footer={<><Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn><Btn onClick={save} data-testid="save-role-btn">Save role</Btn></>}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Role name" required><TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Description"><TextInput value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
        </div>
        <div className="space-y-3 max-h-[45vh] overflow-y-auto pr-1">
          {Object.entries(catalogue).map(([group, perms]) => (
            <div key={group}>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{group}</div>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {Object.entries(perms).map(([k, label]) => (
                  <label key={k} className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded-lg border cursor-pointer ${form.permissions.includes(k) ? "border-seq-green bg-seq-green/5" : "border-slate-200"}`}>
                    <input type="checkbox" checked={form.permissions.includes(k)} onChange={() => toggle(k)} data-testid={`perm-${k}`} className="rounded" />
                    <span className="text-slate-600">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
